import React, { useEffect, useMemo, useRef, useState } from 'react';
import { sankey, sankeyLeft, sankeyRight, sankeyCenter, sankeyJustify } from 'd3-sankey';
import styled from 'styled-components';
import { Node, Link, NodeLabel } from './components';
import NodeTooltip from './components/NodeTooltip';
import LinkTooltip from './components/LinkTooltip';
import NodeDetailModal from './components/NodeDetailModal';
import { type SankeyLink, type SankeyNode } from '@src/pages/journey-map/types';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  position: relative;
  overflow: scroll;
`;

interface SankeyProps {
  data: any;
  width?: number;
  height?: number;
  supportLayers?: boolean;
  align?: 'left' | 'right' | 'center' | 'justify';
  idKey?: string;
  highlightedNodes?: string[];
  selectedNodes?: string[];
  onNodeLabelClick?: (node: any) => void;
}

const Sankey: React.FC<SankeyProps> = ({
  data,
  width: widthProp,
  height: heightProp,
  supportLayers,
  align = 'left',
  idKey,
  highlightedNodes = [],
  selectedNodes = [],
  onNodeLabelClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredNode, setHoveredNode] = useState<SankeyNode | null>(null);
  const [hoveredLink, setHoveredLink] = useState<SankeyLink | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [nodePositions, setNodePositions] = useState<Record<string, { x: number; y: number }>>({});
  const [selectedNode, setSelectedNode] = useState<SankeyNode | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const height = (heightProp ?? 0) < 600 ? 600 : heightProp || 0;
  const width = widthProp ?? 2500;
  const margin = { top: 0, right: 0, bottom: 0, left: 0 };

  const sankeyGenerator = useMemo(() => {
    let alignFunction = sankeyJustify;
    if (align === 'left') {
      alignFunction = sankeyLeft;
    } else if (align === 'right') {
      alignFunction = sankeyRight;
    } else if (align === 'center') {
      alignFunction = sankeyCenter;
    }

    return sankey()
      .nodeWidth(45)
      .nodePadding(80)
      .extent([
        [50, 70],
        [width - margin.left - margin.right, height - margin.top - margin.bottom],
      ])
      .nodeId((node) => {
        if (idKey && node[idKey]) {
          return node[idKey];
        }
        return node.id;
      })
      .nodeAlign(alignFunction)
      .linkSort((prev, next) => {
        const { value: prevValue } = prev;
        const { value: nextValue } = next;
        return nextValue - prevValue;
        // “The shorter the path length, the higher it is.”
        // return (prev.target.depth - prev.source.depth)
        //   - (next.target.depth - next.source.depth);
      })
      .nodeSort((prev, next) => {
        const { value: prevValue } = prev;
        const { value: nextValue } = next;
        return nextValue - prevValue;
      });
  }, [width, height, align]);

  const { nodes, links } = useMemo(() => {
    if (data?.links?.length && data?.nodes?.length) {
      return sankeyGenerator({
        nodes: data.nodes.map((d) => Object.assign({}, d)),
        links: data.links.map((d) => Object.assign({}, d)),
      });
    }
    return { nodes: [], links: [] };
  }, [sankeyGenerator, data]);

  const layoutWidth = useMemo(() => {
    const maxLayer = Math.max(...nodes.map((node) => node.depth));
    const width = (maxLayer + 1) * 350;
    return width;
  }, [nodes]);

  useEffect(() => {
    if (!svgRef.current || !nodes.length) return;

    const svg = svgRef.current;
    const svgRect = svg.getBoundingClientRect();

    const positions: Record<string, { x: number; y: number }> = {};
    nodes.forEach((node) => {
      const nodeX = node.x0 + (node.x1 - node.x0) / 2;
      const nodeY = node.y0;
      positions[node.id] = {
        x: svgRect.left + nodeX,
        y: svgRect.top + nodeY,
      };
    });

    setNodePositions(positions);
  }, [nodes]);

  const handleNodeHover = (node: SankeyNode | null) => {
    setHoveredNode(node);
  };

  const handleLinkHover = (link: SankeyLink | null) => {
    setHoveredLink(link);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };

  const handleNodeDoubleClick = (node: SankeyNode) => {
    setSelectedNode(node);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedNode(null);
  };

  const isLinkHighlighted = (link: SankeyLink) => {
    if (!hoveredLink) return false;
    return link.source.id === hoveredLink.source.id && link.target.id === hoveredLink.target.id;
  };

  return (
    <Container>
      {/* <TransformWrapper
        pinch={{ disabled: false }} // 开启双指缩放
        minScale={0.5}   // 允许缩小到 50%
        maxScale={1}     // 最大放大 4 倍
        initialScale={1}
        centerOnInit
        limitToBounds={false}
        doubleClick={{ disabled: true }}>
        <TransformComponent> */}
      <svg ref={svgRef} width={layoutWidth} height={height} onMouseMove={handleMouseMove}>
        <g transform={`translate(${margin.left},${margin.top})`}>
          <g>
            {links.map((link, i) => (
              <Link
                key={i}
                link={link}
                isHighlighted={isLinkHighlighted(link)}
                onMouseEnter={() => handleLinkHover(link)}
                onMouseLeave={() => handleLinkHover(null)}
              />
            ))}
          </g>
          <g>
            {nodes.map((node, i) => (
              <Node
                key={i}
                node={node}
                onMouseEnter={() => {
                  handleNodeHover(node);
                }}
                onMouseLeave={() => {}}
                onDoubleClick={() => handleNodeDoubleClick(node)}
              />
            ))}
          </g>
        </g>
      </svg>

      {nodes.map((node) => (
        <NodeLabel
          highlightedNodes={highlightedNodes}
          selectedNodes={selectedNodes}
          key={node.id}
          node={node}
          onClick={() => {
            onNodeLabelClick?.(node);
          }}
        />
      ))}
      {hoveredNode && (
        <NodeTooltip
          node={hoveredNode}
          isVisible={true}
          x={mousePosition.x + 20}
          y={mousePosition.y + 20}
        />
      )}
      {hoveredLink && (
        <LinkTooltip
          link={hoveredLink}
          isVisible={true}
          x={mousePosition.x + 20}
          y={mousePosition.y + 20}
        />
      )}
      <NodeDetailModal node={selectedNode} visible={isModalVisible} onClose={handleModalClose} />
      {/* </TransformComponent>
      </TransformWrapper> */}
    </Container>
  );
};

export default Sankey;
